<template>
  <div :class="classContainer">
    <div class="wrapper-features">
      <div class="wrapper-actions">
        <p class="small accent dim-dark">{{ t('label_language') }}</p>
        <ButtonDropdownLanguage v-model="data.languageCode" :is-global :language-default />
      </div>

      <div v-if="hasButtonsCamera" class="wrapper-actions">
        <p class="small accent dim-dark">
          {{ t('subtitle_input_display_camera_label') }}
        </p>
        <div class="wrapper-buttons">
          <button class="button-gray-dim button-full-width" @click="toggleCameraUser">
            {{ textButtonCameraUser }}
          </button>
          <button class="button-gray-dim button-full-width" @click="startCameraDisplay">
            {{ t('subtitle_input_display_camera_label') }}
          </button>
        </div>
      </div>

      <div class="wrapper-radio">
        <SubtitleRadioBackgroundColor v-model="data.subtitleBackgroundColor" :is-global />
        <SubtitleCheckboxLogo v-model="data.subtitleIsVisibleLogo" :is-global />
      </div>

      <div v-if="isModeSubtitle" class="divider-small show-mobile-only" />
      <div v-else class="divider-small" />
    </div>

    <div class="wrapper-features">
      <p class="small accent dim-dark">{{ t('label_text_control') }}</p>
      <GaugeWithLabel v-model="data.textSizeMultiplier" :is-global gauge-setting-global="textSizeMultiplier">
        {{ t('text_size') }}: {{ data.textSizeMultiplier }}
      </GaugeWithLabel>

      <GaugeWithLabel v-model="data.subtitleLineHeight" :is-global gauge-setting-global="subtitleLineHeight">
        {{ t('label_line_height') }}: {{ data.subtitleLineHeight }}
      </GaugeWithLabel>

      <GaugeWithLabel v-model="data.subtitleLineCount" :is-global gauge-setting-global="subtitleLineCount">
        {{ t('label_line_count') }}: {{ data.subtitleLineCount }}
      </GaugeWithLabel>

      <div v-if="isModeSubtitle" class="divider-small show-mobile-only" />
      <div v-else class="divider-small" />
    </div>

    <div class="wrapper-features">
      <p class="small accent dim-dark">{{ t('subtitle_area_control') }}</p>
      <GaugeWithLabel v-model="data.subtitleOpacity" :is-global gauge-setting-global="subtitleOpacity">
        {{ t('subtitle_opacity') }}: {{ data.subtitleOpacity }}
      </GaugeWithLabel>

      <GaugeWithLabel v-model="data.subtitleLocationY" :is-global gauge-setting-global="subtitleLocationY">
        {{ t('label_height') }}: {{ data.subtitleLocationY }}%
      </GaugeWithLabel>

      <div class="wrapper-radio">
        <InputNumberPercent
          v-model="data.subtitleWidth"
          :is-global
          :title-setting="t('subtitle_mode_message_width')"
          :text-value-current="t('subtitle_mode_message_width_current')"
          preferenceTarget="subtitleWidth"
        />
        <SubtitleCheckboxSettingPosition v-model="data.subtitleIsSettingAbove" :is-global />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import SubtitleCheckboxLogo from '@/components/domains/chat/chat-subtitle/SubtitleCheckboxLogo.vue';
import SubtitleCheckboxSettingPosition from '@/components/domains/chat/chat-subtitle/SubtitleCheckboxSettingPosition.vue';
import SubtitleRadioBackgroundColor from '@/components/domains/chat/chat-subtitle/SubtitleRadioBackgroundColor.vue';
import ButtonDropdownLanguage from '@/components/domains/chat/common/ButtonDropdownLanguage.vue';
import GaugeWithLabel from '@/components/domains/chat/common/GaugeWithLabel.vue';
import InputNumberPercent from '@/components/shared/composite/InputNumberPercent.vue';
import { useCamera } from '@/composables/shared/useCamera';
import { getLanguageByCode } from '@/static/languages';
import { useAudioStore } from '@/stores/audio';
import { useUserStore } from '@/stores/user';
import { Language } from '@/types/common';
import { SubtitlePreferences } from '@/types/components';
import { ij_isModeSubtitle } from '@/utils/injectionKeys.js';

const isModeSubtitle = inject<ComputedRef>(ij_isModeSubtitle);

const audioStore = useAudioStore();
const route = useRoute();
const userStore = useUserStore();
const { isCameraOnUser, startCamera, stopCamera } = useCamera();
const { t } = useI18n();

const props = withDefaults(defineProps<{ isGlobal: boolean }>(), {
  isGlobal: false,
});

const data = reactive<SubtitlePreferences>({
  languageCode: null,
  textSizeMultiplier: 1.0,
  subtitleOpacity: 0.8,
  subtitleLocationY: 1,
  subtitleLineHeight: 1.5,
  subtitleLineCount: 3,
  subtitleWidth: 99,
  subtitleBackgroundColor: 'GREEN',
  subtitleIsVisibleLogo: true,
  subtitleIsSettingAbove: true,
});

const classContainer = computed(() => {
  return isModeSubtitle?.value ? 'multi-column' : 'one-column';
});

const payloadMessageFc = computed(() => {
  return {
    generalAttribute: {
      loginId: 'any value',
      languageCode: 'ko',
    },
    subtitleAttribute: {
      ...data,
      languageCode: data.languageCode?.code,
    },
  };
});

// TODO
const hasButtonsCamera = computed(() => {
  return route.meta.chatType === 'SUBTITLE';
});

const textButtonCameraUser = computed(() => {
  return isCameraOnUser.value ? t('camera_turn_off') : t('camera_turn_on');
});

const startCameraUser = () => {
  startCamera('user');
};

const startCameraDisplay = () => {
  startCamera('display');
};

const toggleCameraUser = () => {
  if (isCameraOnUser.value) {
    stopCamera();
  } else {
    startCameraUser();
  }
};

const languageDefault = computed<Language | null>(() => {
  const code = data.languageCode;

  if (!code) return audioStore.languageSelected;

  return typeof code === 'string' ? getLanguageByCode(code) : code;
});

watch(
  () => userStore.userPreferences,
  (newVal) => {
    if (newVal) {
      Object.assign(data, {
        languageCode: newVal.subtitleLanguageCode,
        textSizeMultiplier: newVal.textSizeMultiplier,
        subtitleOpacity: newVal.subtitleOpacity,
        subtitleLocationY: newVal.subtitleLocationY,
        subtitleLineHeight: newVal.subtitleLineHeight,
        subtitleLineCount: newVal.subtitleLineCount,
        subtitleWidth: newVal.subtitleWidth,
        subtitleBackgroundColor: newVal.subtitleBackgroundColor,
        subtitleIsVisibleLogo: newVal.subtitleIsVisibleLogo,
        subtitleIsSettingAbove: newVal.subtitleIsSettingAbove,
      });
    }
  },
  { immediate: true, deep: true, flush: 'post' }
);

defineExpose({
  payloadMessageFc,
});
</script>

<style scoped lang="scss">
.multi-column {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3rem;

  @include respond-to(mobile-landscape) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

.one-column {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.wrapper-features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  border-radius: 14px;
}

.wrapper-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.wrapper-buttons {
  display: flex;
  gap: 1rem;
}

.wrapper-radio {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

button {
  max-height: 3rem;
}
</style>
